import React, { useState, useEffect } from 'react';
import { Linkedin, Loader2, User } from 'lucide-react';
import { teamAPI } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { usePageContent } from '../hooks/usePageContent';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api')) return `${API_BASE_URL}${url}`;
  return `${API_BASE_URL}/api/uploads/image/${url}`;
};

// Helper to get localized content
const getLocalizedField = (item, field, language) => {
  if (language === 'tr') {
    const trField = item[`${field}_tr`];
    if (trField && trField.trim()) return trField;
  }
  return item[field];
};

const Team = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t, language } = useLanguage();
  const { getContent } = usePageContent('team');
  
  // Dynamic page title
  usePageTitle('nav.team', 'Team', 'Takım');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const teamData = await teamAPI.getAll();
        setTeamMembers(teamData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" style={{ background: '#0F172A' }}>
        <Loader2 className="w-12 h-12 animate-spin" style={{ color: '#00F0FF' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" style={{ background: '#0F172A' }}>
        <div className="text-center">
          <p className="body-large mb-4" style={{ color: '#F8FAFC' }}>{t('team.error.loadFailed')}</p>
          <p className="body-small" style={{ color: '#94A3B8' }}>{error}</p>
        </div>
      </div>
    );
  }

  // Check for advisor in both EN and TR versions
  const advisor = teamMembers.find(m => 
    m.role?.toLowerCase().includes('advisor') || 
    m.role?.toLowerCase().includes('faculty') ||
    m.role_tr?.toLowerCase().includes('danışman')
  );
  const members = teamMembers.filter(m => 
    !m.role?.toLowerCase().includes('advisor') && 
    !m.role?.toLowerCase().includes('faculty') &&
    !m.role_tr?.toLowerCase().includes('danışman')
  );

  return (
    <div className="min-h-screen pt-20" style={{ background: '#0F172A' }} data-testid="team-page">
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${getContent('hero_image') || 'https://images.unsplash.com/photo-1640163561346-7778a2edf353?w=1920'}')`
          }}
        >
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.85) 0%, rgba(30, 41, 59, 0.7) 50%, rgba(0, 240, 255, 0.3) 100%)'
          }}></div>
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-[7.6923%] text-center">
          <h1 className="display-huge mb-4" style={{
            color: '#F8FAFC',
            textShadow: '0 0 20px rgba(0, 240, 255, 0.5)'
          }}>{getContent('hero_title')}</h1>
          <p className="body-large" style={{ color: '#F8FAFC', opacity: 0.9 }}>
            {getContent('hero_subtitle')}
          </p>
        </div>
      </section>

      {/* Advisor Section */}
      {advisor && (
        <section className="py-24 px-[7.6923%]" style={{
          background: 'rgba(30, 41, 59, 0.4)',
          backdropFilter: 'blur(8px)'
        }}>
          <div className="max-w-[1400px] mx-auto">
            <h2 className="display-medium mb-12 text-center" style={{ color: '#F8FAFC' }}>
              {getContent('advisor_title')}
            </h2>
            
            <div className="max-w-2xl mx-auto">
              <div className="glass-card p-8 text-center dark-transition dark-hover" data-testid="advisor-card">
                <div 
                  className="w-48 h-48 mx-auto mb-6 overflow-hidden"
                  style={{
                    clipPath: 'polygon(0% 0%, 100% 0%, 100% 85%, 50% 100%, 0% 85%)',
                    boxShadow: '0 0 20px rgba(0, 240, 255, 0.3)'
                  }}
                >
                  {advisor.image ? (
                    <img 
                      src={getImageUrl(advisor.image)} 
                      alt={advisor.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center" style={{ background: 'rgba(0, 240, 255, 0.2)' }}>
                      <User size={64} style={{ color: '#00F0FF' }} />
                    </div>
                  )}
                </div>
                <h3 className="heading-1 mb-2" style={{ color: '#F8FAFC' }}>{advisor.name}</h3>
                <p className="body-medium mb-4" style={{
                  color: '#00F0FF',
                  textShadow: '0 0 10px rgba(0, 240, 255, 0.4)'
                }}>{getLocalizedField(advisor, 'role', language)}</p>
                <p className="body-medium leading-relaxed mb-6" style={{ color: '#F8FAFC', opacity: 0.9 }}>
                  {getLocalizedField(advisor, 'bio', language)}
                </p>
                {advisor.linkedin && advisor.linkedin !== '#' && (
                  <a href={advisor.linkedin} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 hover:text-[#00F0FF] transition-colors duration-300"
                    style={{ color: '#F8FAFC' }}>
                    <Linkedin size={24} />
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Team Members Grid */}
      <section className="py-24 px-[7.6923%]" style={{ background: '#0F172A' }}>
        <div className="max-w-[1400px] mx-auto">
          <h2 className="display-medium mb-12 text-center" style={{ color: '#F8FAFC' }}>
            {getContent('members_title')}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {members.map((member) => (
              <div key={member.id} className="glass-card overflow-hidden dark-transition dark-hover"
                data-testid={`team-member-card-${member.id}`}>
                <div className="h-64 relative overflow-hidden" style={{ background: 'rgba(0, 240, 255, 0.1)' }}>
                  {member.image ? (
                    <img 
                      src={getImageUrl(member.image)} 
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User size={64} style={{ color: '#00F0FF' }} />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-transparent to-transparent"></div>
                </div>
                <div className="p-6">
                  <h3 className="heading-2 mb-2" style={{ color: '#F8FAFC' }}>{member.name}</h3>
                  <p className="body-small mb-4 min-h-[3rem]" style={{
                    color: '#00F0FF',
                    textShadow: '0 0 8px rgba(0, 240, 255, 0.3)'
                  }}>{getLocalizedField(member, 'role', language)}</p>
                  <p className="body-small leading-relaxed mb-4" style={{ color: '#F8FAFC', opacity: 0.85 }}>
                    {getLocalizedField(member, 'bio', language)}
                  </p>
                  {member.linkedin && member.linkedin !== '#' && (
                    <a href={member.linkedin} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 hover:text-[#00F0FF] transition-colors duration-300"
                      style={{ color: '#F8FAFC' }}>
                      <Linkedin size={20} />
                      <span className="body-small">{t('team.connect')}</span>
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Stats */}
      <section className="py-24 px-[7.6923%]" style={{
        background: 'rgba(30, 41, 59, 0.4)',
        backdropFilter: 'blur(8px)'
      }}>
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center p-8 glass-card dark-transition dark-hover">
              <p className="display-large mb-2" style={{ color: '#00F0FF', textShadow: '0 0 15px rgba(0, 240, 255, 0.5)' }}>{teamMembers.length}</p>
              <p className="body-medium" style={{ color: '#F8FAFC' }}>{t('team.stats.members')}</p>
            </div>
            <div className="text-center p-8 glass-card dark-transition dark-hover">
              <p className="display-large mb-2" style={{ color: '#00F0FF', textShadow: '0 0 15px rgba(0, 240, 255, 0.5)' }}>4</p>
              <p className="body-medium" style={{ color: '#F8FAFC' }}>{t('team.stats.departments')}</p>
            </div>
            <div className="text-center p-8 glass-card dark-transition dark-hover">
              <p className="display-large mb-2" style={{ color: '#00F0FF', textShadow: '0 0 15px rgba(0, 240, 255, 0.5)' }}>100+</p>
              <p className="body-medium" style={{ color: '#F8FAFC' }}>{t('team.stats.engineeringHours')}</p>
            </div>
            <div className="text-center p-8 glass-card dark-transition dark-hover">
              <p className="display-large mb-2" style={{ color: '#00F0FF', textShadow: '0 0 15px rgba(0, 240, 255, 0.5)' }}>1</p>
              <p className="body-medium" style={{ color: '#F8FAFC' }}>{t('team.stats.missionGoal')}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Team;
